<template>
  <KekkleBottomSheet
    v-model:open="isOpen"
    shadow="teal"
    padding="normal"
    initial-height="half"
  >
    <div v-if="member" class="dark:text-slate-200">
      <!-- Profile Header -->
      <div class="mb-6 flex items-start gap-4">
        <!-- Profile Picture -->
        <div
          class="h-24 w-24 flex-shrink-0 overflow-hidden rounded-2xl border-2 border-black-dark shadow-teal-hard"
        >
          <transition name="opacity">
            <img
              v-if="pictureUrl || thumbUrl"
              draggable="false"
              alt="Profile Picture"
              class="h-full w-full object-cover"
              :src="(thumbUrl || pictureUrl) ?? ''"
            />
          </transition>
        </div>

        <!-- Info Column -->
        <div class="flex flex-1 flex-col justify-center gap-2">
          <div>
            <h2 class="text-2xl font-bold dark:text-slate-200">
              {{ member.name }}
            </h2>
            <span
              class="mt-1 inline-block rounded-full px-3 py-1 text-xs font-medium"
              :class="memberClass"
            >
              {{ memberText }}
            </span>
          </div>
          <p v-if="birthdayDate" class="text-sm dark:text-slate-200">
            🎂 {{ birthdayString }}<span v-if="age"> ({{ age }})</span>
          </p>
        </div>
      </div>

      <!-- Stats Row - Compact -->
      <div v-if="!statsError" class="mb-6 flex flex-wrap gap-2">
        <!-- Quotes -->
        <div
          class="flex min-w-[calc(50%-0.5rem)] flex-1 cursor-pointer items-center gap-3 rounded-lg border-2 border-black-dark bg-white p-3 shadow-red-hard-sm transition-all hover:shadow-none dark:bg-kekkle-dark-light"
          :class="{ 'opacity-50': loadingStats }"
          @click="goToQuotes"
        >
          <span class="text-3xl">📣</span>
          <div class="min-w-0 flex-1">
            <p class="text-2xl font-bold leading-none">{{ numberOfQuotes }}</p>
            <p
              class="truncate text-xs capitalize text-gray-600 dark:text-gray-400"
            >
              {{ $t("global.quote", numberOfQuotes) }}
            </p>
          </div>
        </div>

        <!-- Photos -->
        <div
          class="flex min-w-[calc(50%-0.5rem)] flex-1 items-center gap-3 rounded-lg border-2 border-black-dark bg-white p-3 shadow-purple-hard-sm dark:bg-kekkle-dark-light"
          :class="{ 'opacity-50': loadingStats }"
        >
          <span class="text-3xl">📷</span>
          <div class="min-w-0 flex-1">
            <p class="text-2xl font-bold leading-none">{{ numberOfPhotos }}</p>
            <p
              class="truncate text-xs capitalize text-gray-600 dark:text-gray-400"
            >
              {{ $t("global.photos", numberOfPhotos) }}
            </p>
          </div>
        </div>

        <!-- Shorts -->
        <div
          class="flex min-w-[calc(50%-0.5rem)] flex-1 cursor-pointer items-center gap-3 rounded-lg border-2 border-black-dark bg-white p-3 shadow-yellow-hard-sm transition-all hover:shadow-none dark:bg-kekkle-dark-light"
          :class="{ 'opacity-50': loadingStats }"
          @click="goToShorts"
        >
          <span class="text-3xl">🎬</span>
          <div class="min-w-0 flex-1">
            <p class="text-2xl font-bold leading-none">{{ numberOfShorts }}</p>
            <p
              class="truncate text-xs capitalize text-gray-600 dark:text-gray-400"
            >
              {{ $t("global.shorts", numberOfShorts) }}
            </p>
          </div>
        </div>

        <!-- Events -->
        <div
          class="flex min-w-[calc(50%-0.5rem)] flex-1 items-center gap-3 rounded-lg border-2 border-black-dark bg-white p-3 shadow-blue-hard-sm dark:bg-kekkle-dark-light"
          :class="{ 'opacity-50': loadingStats }"
        >
          <span class="text-3xl">📅</span>
          <div class="min-w-0 flex-1">
            <p class="text-xl font-bold leading-none">
              {{ numberOfEvents
              }}<span
                class="text-sm font-normal text-gray-600 dark:text-gray-400"
                >/{{ totalNumberOfEvents }}</span
              >
            </p>
            <p
              class="truncate text-xs capitalize text-gray-600 dark:text-gray-400"
            >
              {{ $t("pages.memberView.presence") }}
            </p>
          </div>
        </div>
      </div>

      <!-- Error State -->
      <InformationText
        v-else
        :information="$t('pages.memberView.stats_error')"
        class="mb-6"
      ></InformationText>

      <!-- Latest Quote -->
      <div
        v-if="latestQuote !== null"
        class="rounded-lg border-2 border-black-dark bg-gray-50 p-4 shadow-yellow-hard-sm dark:bg-kekkle-dark-light"
      >
        <p
          class="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400"
        >
          ✨ {{ $t("pages.memberView.newest_quote") }}
        </p>
        <p
          class="font-patrick-hand text-lg italic leading-relaxed dark:text-slate-200"
        >
          "{{ latestQuote }}"
        </p>
      </div>
    </div>
  </KekkleBottomSheet>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useUserStore } from "@/stores/modules/user/user";
import { useGroupsStore } from "@/stores/modules/groups/groups";
import { useQuoteFilterStore } from "@/stores/modules/quotes/quotes-filter";
import { useShortFilterStore } from "@/stores/modules/shorts/shorts-filter";
import { ImageSizes } from "@/enums/images-sizes";
import { getMemberStats } from "@/helpers/firestore/MemberStatsService";
import { formatBirthdayString, calculateAge } from "@/helpers/dates/birthday";
import {
  getPermissionBadgeClass,
  getPermissionBadgeI18nKey,
} from "@/helpers/ui/permission-badge";
import KekkleBottomSheet from "./ui/KekkleBottomSheet.vue";
import InformationText from "./InformationText.vue";

type Props = {
  memberId: string | null;
};

const props = defineProps<Props>();
const router = useRouter();
const { t } = useI18n();
const userStore = useUserStore();
const groupsStore = useGroupsStore();

const isOpen = defineModel<boolean>("open", { required: true });

const numberOfQuotes = ref(0);
const numberOfEvents = ref(0);
const numberOfPhotos = ref(0);
const numberOfShorts = ref(0);
const latestQuote = ref<string | null>(null);
const loadingStats = ref(false);
const statsError = ref(false);

const member = computed(() => {
  if (!props.memberId) return null;
  return groupsStore.activeGroupMembersSorted.find(
    (m) => m.uuid === props.memberId,
  );
});

const thumbUrl = computed(() => {
  if (!props.memberId) return null;
  return groupsStore.activeGroupMembersProfilePictureData?.[props.memberId]?.[
    ImageSizes.Thumb
  ]?.["url"];
});

const pictureUrl = computed(() => {
  if (!props.memberId) return null;
  return groupsStore.activeGroupMembersProfilePictureData?.[props.memberId]?.[
    ImageSizes.HD
  ]?.["url"];
});

const birthdayDate = computed(() => {
  if (!member.value?.birthday) return null;
  return member.value.birthday;
});

const birthdayString = computed(() => {
  if (!birthdayDate.value) return "";
  return formatBirthdayString(birthdayDate.value);
});

const age = computed(() => {
  if (!birthdayDate.value) return "";
  return calculateAge(birthdayDate.value);
});

const memberClass = computed(() => {
  return getPermissionBadgeClass(member.value?.permission);
});

const memberText = computed(() => {
  const i18nKey = getPermissionBadgeI18nKey(member.value?.permission);
  return i18nKey ? t(i18nKey) : "";
});

const totalNumberOfEvents = computed(() => {
  return groupsStore.groupData?.general_data?.number_of_events;
});

// Watch for member changes and fetch data
watch(
  () => props.memberId,
  (newMemberId) => {
    if (newMemberId && isOpen.value) {
      loadMemberData();
    }
  },
  { immediate: true },
);

// Watch for sheet opening and fetch data
watch(isOpen, (newValue) => {
  if (newValue && props.memberId) {
    loadMemberData();
  } else {
    // Reset data when closing
    numberOfQuotes.value = 0;
    numberOfEvents.value = 0;
    numberOfPhotos.value = 0;
    numberOfShorts.value = 0;
    latestQuote.value = null;
    statsError.value = false;
    loadingStats.value = false;
  }
});

async function loadMemberData() {
  if (!props.memberId || !userStore.activeGroup) return;

  // Download profile pictures if not cached
  if (!thumbUrl.value) {
    groupsStore.downloadGroupMemberProfilePictureData({
      uuid: props.memberId,
      format: ImageSizes.Thumb,
    });
  }

  if (!pictureUrl.value) {
    groupsStore.downloadGroupMemberProfilePictureData({
      uuid: props.memberId,
      format: ImageSizes.HD,
    });
  }

  // Fetch all stats in parallel with error handling
  loadingStats.value = true;
  statsError.value = false;

  try {
    const stats = await getMemberStats(userStore.activeGroup, props.memberId);
    numberOfQuotes.value = stats.quoteCount;
    numberOfPhotos.value = stats.photoCount;
    numberOfShorts.value = stats.shortCount;
    numberOfEvents.value = stats.eventCount;
    latestQuote.value = stats.latestQuote;
  } catch (error) {
    console.error("Failed to load member stats:", error);
    statsError.value = true;
    // Reset stats to 0 on error
    numberOfQuotes.value = 0;
    numberOfPhotos.value = 0;
    numberOfShorts.value = 0;
    numberOfEvents.value = 0;
    latestQuote.value = null;
  } finally {
    loadingStats.value = false;
  }
}

async function goToQuotes() {
  if (numberOfQuotes.value === 0 || !props.memberId) return;

  // Set author filter in store
  const quoteFilterStore = useQuoteFilterStore();
  quoteFilterStore.setAuthorFilter(props.memberId);

  // Close the bottom sheet first (cleans up the fake history entry via go(-1))
  isOpen.value = false;

  // Wait for the history cleanup to complete, then navigate
  setTimeout(() => {
    router.push({ name: "Quotes" });
  }, 50);
}

async function goToShorts() {
  if (numberOfShorts.value === 0 || !props.memberId) return;

  // Set author filter in store
  const shortFilterStore = useShortFilterStore();
  shortFilterStore.setAuthorFilter(props.memberId);

  // Close the bottom sheet first (cleans up the fake history entry via go(-1))
  isOpen.value = false;

  // Wait for the history cleanup to complete, then navigate
  setTimeout(() => {
    router.push({
      name: "Shorts",
      query: { author: props.memberId },
    });
  }, 50);
}
</script>

<style scoped lang="scss">
.opacity-enter-active {
  transition: opacity 0.5s ease;
}

.opacity-leave-active {
  position: absolute;
  transition: opacity 0.5s ease;
}

.opacity-enter-from,
.opacity-leave-to {
  opacity: 0;
}
</style>
